import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { logError } from '@cortex/faro';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

/** @graph-connects none */
const searchSchema = z.object({
  redirect: z.string().optional(),
  error: z.string().optional(),
});

/** 認証後の既定の着地先。redirect が無い / 信用できないときはここへ送る */
/** @graph-connects none */
const DEFAULT_REDIRECT = '/dashboard';

/**
 * `redirect` クエリは URL から来るので、そのまま navigate すると
 * `?redirect=https://evil.example` でオープンリダイレクトに使える。
 * 文字パターンの列挙（`//` 決め打ち → `\` 混在 → タブ/改行混在 ...）は
 * WHATWG URL の正規化ルールを後追いし続けることになり切りがないため、
 * `URL` で実際にパースしてオリジンを比較する。ブラウザ自身が使うのと同じ
 * 正規化ルールに乗るため、除去対象文字や encode パターンを個別に潰す必要がない。
 *
 * @graph-connects @cortex/faro [calls] URL パース失敗を Faro にエラーログ送信
 */
function safeRedirect(redirect: string | undefined): string {
  if (redirect === undefined) return DEFAULT_REDIRECT;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return DEFAULT_REDIRECT;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    // redirect クエリが URL としてパースできない不正な値。フォールバックさせつつ
    // 「不正な redirect を受け取った」事実を観測できるようにしておく
    logError(error instanceof Error ? error : new Error(String(error)), {
      event: 'safe_redirect_parse_failed',
    });
    return DEFAULT_REDIRECT;
  }
}

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business ログインページ。Googleアカウント認証ボタン・エラー表示・グラデーションオーブ背景。認証済みで開かれた場合は redirect（既定は /dashboard）へ送り返す
 * @graph-connects useAuth [calls] UseAuthを呼び出す
 */
export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

/** @graph-connects none */
function LoginPage() {
  const { login, isLoggingIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { error, redirect } = Route.useSearch();

  // OAuth の戻り先は `buildEdgeAuthStartUrl(window.location.href)` = このログインページ
  // 自身なので、認証が通った後もここに着地する。送り返さないとログイン画面から出られない
  // （本番で発生。`/login?redirect=%2Fdashboard` から先に進めなかった）。
  //
  // navigate は 1 回だけ撃つ。auth state の transition 中に effect が再発火して
  // 連続 navigate すると TanStack Router の setIsTransitioning が連発し、
  // React の setState 上限を超えて Minified React error #185 になる。
  const navigatedRef = useRef(false);
  useEffect(() => {
    if (navigatedRef.current) return;
    if (!isAuthenticated) return;
    navigatedRef.current = true;
    void navigate({ to: safeRedirect(redirect) });
  }, [isAuthenticated, redirect, navigate]);

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ background: 'var(--color-bg-base)' }}
    >
      {/* 背景グラデーションオーブ */}
      <div
        className="gradient-orb"
        style={{
          width: 500,
          height: 500,
          top: '-10%',
          left: '-10%',
          background: 'var(--color-primary)',
        }}
      />
      <div
        className="gradient-orb"
        style={{
          width: 400,
          height: 400,
          bottom: '-10%',
          right: '-5%',
          background: 'var(--color-accent)',
          animationDelay: '3s',
        }}
      />
      <div
        className="gradient-orb"
        style={{
          width: 300,
          height: 300,
          top: '40%',
          right: '20%',
          background: 'var(--color-secondary)',
          animationDelay: '6s',
        }}
      />

      {/* ログインカード */}
      <div
        className="glass-card-elevated relative z-10 w-full max-w-md p-8"
        style={{ animation: 'fadeInUp 0.6s ease' }}
      >
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Quality Atelier
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            QA Group KPI Dashboard
          </p>
        </div>

        {error && (
          <div
            className="mb-4 rounded-lg p-3 text-sm"
            style={{
              background: 'var(--color-error-muted)',
              color: 'var(--color-error)',
            }}
          >
            {decodeURIComponent(error)}
          </div>
        )}

        <button
          onClick={() => {
            login();
          }}
          disabled={isLoggingIn}
          className="btn btn-primary btn-lg w-full"
        >
          {isLoggingIn ? (
            <span className="loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
          ) : (
            <>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Googleアカウントでログイン
            </>
          )}
        </button>

        <p className="mt-6 text-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
          @air-closet.com アカウントでログインしてください
        </p>
      </div>
    </div>
  );
}
